import { createEmptyRows } from './helpers';
import type { SampleManagerAction, SampleManagerState } from './types';

// Re-export from helpers for backward compatibility
export { createEmptyRows };

/**
 * Reducer for managing SampleManager state
 */
export const sampleManagerReducer = (state: SampleManagerState, action: SampleManagerAction): SampleManagerState => {
  switch (action.type) {
    case 'UPLOAD_START':
      return {
        ...state,
        isProcessing: true,
        errorMessage: null,
      };

    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        hasFileBeenUploaded: true,
        rowData: action.payload.rowData,
        errorMessage: null,
        isProcessing: false,
      };

    // Syncs rowData from form state (e.g. draft load) without setting hasFileBeenUploaded,
    // preserving the ability to change the sample count after the draft is populated.
    case 'SYNC_FROM_FORM':
      return {
        ...state,
        rowData: action.payload.rowData,
      };

    case 'UPLOAD_ERROR':
      return {
        ...state,
        hasFileBeenUploaded: false,
        errorMessage: action.payload.errorMessage,
        isProcessing: false,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        errorMessage: null,
      };

    case 'UPDATE_ROW_COUNT':
      return {
        ...state,
        rowData: createEmptyRows(action.payload.count),
      };

    case 'UPDATE_ROW': {
      const { rowIndex, patch } = action.payload;
      const updatedRows = state.rowData.map((row, i) => (i === rowIndex ? { ...row, ...patch } : row));
      return {
        ...state,
        rowData: updatedRows,
      };
    }

    case 'RESIZE_ROWS': {
      const { count, existingRows } = action.payload;
      // Generate all rows with correct well positions and demux indices for the new count
      const allGeneratedRows = createEmptyRows(count);
      // Overlay existing user data onto the generated rows, preserving entered values
      const mergedRows = allGeneratedRows.map((generatedRow, i) => {
        if (i < existingRows.length) {
          // Preserve user-provided fields (name, study, org, etc.) from existingRows,
          // but always use freshly generated well_position and demux_index so that
          // position metadata is recalculated correctly for the new total count.
          return {
            ...existingRows[i],
            well_position: generatedRow.well_position,
            demux_index: generatedRow.demux_index,
          };
        }
        // New slot — use the generated row with correct position metadata
        return generatedRow;
      });
      return {
        ...state,
        rowData: mergedRows,
      };
    }

    case 'RESET':
      return {
        hasFileBeenUploaded: false,
        rowData: createEmptyRows(4),
        errorMessage: null,
        isProcessing: false,
      };

    default:
      return state;
  }
};
